import { useWaitFor } from '@ifixit/ui/hooks/use-wait-for';
import { useEffect } from 'react';
import { hasUserConsentedFromCookie } from './consent-storage';

// https://shopify.dev/docs/api/customer-privacy#installation-on-a-custom-storefront
export type ShopifyAPI = {
   customerPrivacy: {
      consentStatus?: 'loading' | 'loaded';
      analyticsProcessingAllowed(): boolean;
      marketingAllowed(): boolean;
      saleOfDataAllowed(): boolean;
      currentVisitorConsent(): {
         marketing: '' | 'yes' | 'no';
         analytics: '' | 'yes' | 'no';
         preferences: '' | 'yes' | 'no';
         sale_of_data: '' | 'yes' | 'no';
      };
      getTrackingConsentMetafield(key: string): string | null;
      setTrackingConsent(
         props: {
            marketing?: boolean;
            analytics?: boolean;
            preferences?: boolean;
            sale_of_data?: boolean;
            headlessStorefront: boolean;
            checkoutRootDomain: string;
            storefrontRootDomain: string;
            storefrontAccessToken: string;
            metafields?: { key: string; value: string | null }[];
         },
         callback?: () => unknown
      ): void;
   };
};

export type ShopifyConfig = {
   checkout: string;
   storefront: string;
   accessToken: string;
   /**
    * Load consent through our Storefront API proxy, as Shopify now expects of
    * headless storefronts, so Shopify sets first-party cookies and knows the
    * visitor's region.
    */
   consentThroughProxy?: boolean;
};

const CONSENT_API =
   'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js';
const CONSENT_API_THROUGH_PROXY =
   'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.2/consent-tracking-api.js';
const CONSENT_API_SCRIPT_ID = 'shopify-customer-privacy-api';

declare global {
   interface Window {
      Shopify?: ShopifyAPI;
   }
}

interface ShopifyConsentProps {
   piwikSiteId: string;
   shopifyConfig: ShopifyConfig;
   needsConsent: boolean;
}

/**
 * Syncs consent state to Shopify when the API is available and parameters change.
 * Safe to call multiple times - will only update if consent state differs.
 */
export function useSyncShopifyConsent({
   piwikSiteId,
   shopifyConfig,
   needsConsent,
}: ShopifyConsentProps) {
   const { checkout, storefront, accessToken, consentThroughProxy } = shopifyConfig;
   const shopifyReady = useWaitFor(() => isConsentLoaded(consentThroughProxy));
   useEffect(() => {
      if (!shopifyReady) return;

      const userConsent = hasUserConsentedFromCookie(piwikSiteId) ?? !needsConsent;
      if (!shopifyConsentMatches(userConsent))
         updateShopifyConsent(userConsent, {
            checkout,
            storefront,
            accessToken,
            consentThroughProxy,
         });
   }, [
      shopifyReady,
      piwikSiteId,
      checkout,
      storefront,
      accessToken,
      consentThroughProxy,
      needsConsent,
   ]);
}

/**
 * Loads Shopify's Customer Privacy API. Through the proxy, it needs its config
 * before the script runs, so the script loads after hydration instead of
 * from the server-rendered page.
 */
export function ShopifyConsentScript({ accessToken, consentThroughProxy }: ShopifyConfig) {
   useEffect(() => {
      if (!consentThroughProxy || document.getElementById(CONSENT_API_SCRIPT_ID)) return;
      const shopify = ((
         window as { Shopify?: { customerPrivacy?: { config?: object } } }
      ).Shopify ??= {});
      const customerPrivacy = (shopify.customerPrivacy ??= {});
      // As Hydrogen's useCustomerPrivacy sets up the API: consentDomain sends
      // its consent requests to /api/<version>/graphql.json on this domain.
      customerPrivacy.config = {
         ...customerPrivacy.config,
         isHeadless: true,
         asyncConsent: true,
         asyncVisitorState: true,
         consentDomain: window.location.host,
         storefrontAccessToken: accessToken,
      };
      const script = document.createElement('script');
      script.id = CONSENT_API_SCRIPT_ID;
      script.src = CONSENT_API_THROUGH_PROXY;
      script.async = true;
      document.head.append(script);
   }, [accessToken, consentThroughProxy]);

   return consentThroughProxy ? null : <script async src={CONSENT_API} />;
}

function isConsentLoaded(consentThroughProxy: boolean | undefined) {
   const customerPrivacy = window.Shopify?.customerPrivacy;
   if (typeof customerPrivacy?.setTrackingConsent !== 'function') return false;
   return !consentThroughProxy || customerPrivacy.consentStatus === 'loaded';
}

export function updateShopifyConsent(accepted: boolean, shopifyConfig: ShopifyConfig) {
   const customerPrivacy = window.Shopify?.customerPrivacy;
   if (typeof customerPrivacy?.setTrackingConsent !== 'function') return;
   customerPrivacy.setTrackingConsent({
      'marketing': accepted,
      'analytics': accepted,
      'preferences': accepted,
      'sale_of_data': accepted,
      'headlessStorefront': true,
      // Through the proxy, Shopify keeps consent in cookies on this domain.
      'checkoutRootDomain': shopifyConfig.consentThroughProxy
         ? window.location.host
         : shopifyConfig.checkout,
      'storefrontRootDomain': shopifyConfig.storefront,
      'storefrontAccessToken': shopifyConfig.accessToken,
   });
}

function shopifyConsentMatches(userConsent: boolean) {
   const currentVisitorConsent = window.Shopify?.customerPrivacy?.currentVisitorConsent();
   if (!currentVisitorConsent) return false;

   const consentChoice = userConsent ? 'yes' : 'no';
   const { analytics, marketing, preferences, sale_of_data } = currentVisitorConsent;
   return (
      analytics === consentChoice &&
      marketing === consentChoice &&
      preferences === consentChoice &&
      sale_of_data === consentChoice
   );
}
